import { makeStyles, mergeClasses } from '@fluentui/react-components';

import { spacing } from '@/theme/tokens';

const SEV_LABEL: Record<number, string> = {
  0: 'Sev 0',
  1: 'Sev 1',
  2: 'Sev 2',
  3: 'Sev 3',
  4: 'Sev 4',
};

/** Foreground and background colour per severity level following Azure Monitor conventions:
 *  0=Critical (red), 1=High (orange), 2=Medium (yellow), 3=Low (blue), 4=Info (grey) */
const SEV_COLORS: Record<
  number,
  { fg: string; bg: string }
> = {
  0: { fg: 'var(--colorPaletteRedForeground1)', bg: 'var(--colorPaletteRedBackground2)' },
  1: { fg: 'var(--colorPaletteDarkOrangeForeground1)', bg: 'var(--colorPaletteDarkOrangeBackground2)' },
  2: { fg: 'var(--colorPaletteYellowForeground1)', bg: 'var(--colorPaletteYellowBackground2)' },
  3: { fg: 'var(--colorPaletteBlueForeground2)', bg: 'var(--colorPaletteBlueBackground2)' },
  4: { fg: 'var(--colorNeutralForeground3)', bg: 'var(--colorNeutralBackground3)' },
};

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    paddingLeft: spacing.s,
    paddingRight: spacing.s,
    paddingTop: spacing.xxs,
    paddingBottom: spacing.xxs,
    borderRadius: 'var(--borderRadiusCircular)',
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  dot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
});

interface SeverityBadgeProps {
  severity: number;
  className?: string;
}

export function SeverityBadge({ severity, className }: SeverityBadgeProps) {
  const classes = useStyles();
  const colors = SEV_COLORS[severity] ?? SEV_COLORS[4];
  const label = SEV_LABEL[severity] ?? `Sev ${String(severity)}`;
  return (
    <span
      className={mergeClasses(classes.root, className)}
      style={{ color: colors.fg, backgroundColor: colors.bg }}
      title={label}
    >
      <span className={classes.dot} style={{ backgroundColor: colors.fg }} aria-hidden />
      {label}
    </span>
  );
}
